import React from "react";
import Select, {
	components,
	GroupBase,
	OptionProps,
	GroupHeadingProps,
	ValueContainerProps,
	StylesConfig,
} from "react-select";
import { Control, FieldValues, Path, useController } from "react-hook-form";
import { filtersConfig } from "@src/utils/reactSelectUtils";

export interface SelectOption<V extends string | number = number> {
	label: string;
	value: V;
	isActive?: boolean;
	isDisabled?: boolean;
}

interface MultiSelectYupProps<T extends FieldValues> {
	control: Control<T>;
	name: Path<T>;
	options: Array<SelectOption<string>> | Array<GroupBase<SelectOption<string>>>;
	placeholder?: string;
	isLoading?: boolean;
	isDisabled?: boolean;
	closeMenuOnSelect?: boolean;
	isClearable?: boolean;
	maxSelected?: number;
}

const CheckboxOption = <V extends string | number, >(
	props: OptionProps<SelectOption<V>, true, GroupBase<SelectOption<V>>>,
) => {
	const { isSelected, data } = props;
	const inactive = data.isActive === false;

	return (
		<components.Option {...props}>
			<div style={{ display: "flex", alignItems: "center", gap: 8, opacity: inactive ? 0.55 : 1 }}>
				<input type="checkbox" checked={isSelected} readOnly />
				<span>{data.label}</span>
			</div>
		</components.Option>
	);
};

export const DefaultGroupHeading = <V extends string | number, >(
	props: GroupHeadingProps<SelectOption<V>, true, GroupBase<SelectOption<V>>>,
) => {
	if (!props.children) return null;

	return (
		<components.GroupHeading {...props}>
			<div
				style={{
					fontSize: 11,
					fontWeight: 600,
					letterSpacing: "0.04em",
					opacity: 0.6,
					padding: "2px 2px",
				}}
			>
				{props.children}
			</div>
		</components.GroupHeading>
	);
};

const HiddenMultiValue = () => null;

const CountValueContainer = (
	props: ValueContainerProps<SelectOption<string>, true, GroupBase<SelectOption<string>>>,
) => {
	const count = props.getValue().length;
	const inputValue = props.selectProps.inputValue ?? "";
	const isTyping = inputValue.length > 0;

	return (
		<components.ValueContainer {...props}>
			{count > 0 && !isTyping && (
				<div
					style={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						minWidth: 20,
						height: 20,
						padding: "0 6px",
						marginRight: 6,
						borderRadius: 10,
						backgroundColor: "#E0E0E0",
						color: "#555",
						fontSize: 12,
						fontWeight: 600,
						lineHeight: 1,
					}}
				>
					{count}
				</div>
			)}
			{props.children}
		</components.ValueContainer>
	);
};

function isGroupArray<V extends string | number>(
	options: Array<SelectOption<V>> | Array<GroupBase<SelectOption<V>>>,
): options is Array<GroupBase<SelectOption<V>>> {
	return options.length > 0 && "options" in (options[0] as GroupBase<SelectOption<V>>);
}

function flattenOptions<V extends string | number>(
	options: Array<SelectOption<V>> | Array<GroupBase<SelectOption<V>>>,
): Array<SelectOption<V>> {
	if (!options.length) return [];
	return isGroupArray(options) ? options.flatMap((g) => g.options) : options;
}

export const MultiSelectYup = <T extends FieldValues>({
																			control,
																			name,
																			options,
																			placeholder,
																			isLoading,
																			isDisabled,
																			closeMenuOnSelect = false,
																			isClearable = true,
																			maxSelected,
																		}: MultiSelectYupProps<T>) => {

	const { field, fieldState } = useController({ name, control });

	const valueArray = React.useMemo(() => {
		return (Array.isArray(field.value) ? field.value : []) as Array<string>;
	}, [field.value]);

	const flat = React.useMemo(() => flattenOptions(options), [options]);

	const selectedOptions = React.useMemo(() => {
		if (!valueArray.length) return [];
		const set = new Set(valueArray);
		return flat.filter((o) => set.has(o.value));
	}, [flat, valueArray]);

	const { isDirty, invalid } = fieldState;
	const innerTernary = valueArray.length && isDirty ? "success" : "none";
	const inputStatus = invalid ? "error" : innerTernary;

	const customStyles = filtersConfig(inputStatus, "m") as StylesConfig<
		SelectOption<string>,
		true,
		GroupBase<SelectOption<string>>
	>;

	return (
		<Select<SelectOption<string>, true, GroupBase<SelectOption<string>>>
			isMulti
			isClearable={isClearable}
			isDisabled={isDisabled ?? false}
			isLoading={isLoading ?? false}
			closeMenuOnSelect={closeMenuOnSelect}
			hideSelectedOptions={false}
			options={options}
			placeholder={placeholder}
			styles={customStyles}
			components={{
				IndicatorSeparator: null,
				Option: CheckboxOption,
				GroupHeading: DefaultGroupHeading,
				ValueContainer: CountValueContainer,
				MultiValue: HiddenMultiValue,
				MultiValueRemove: HiddenMultiValue,
			}}
			onChange={(data) => {
				const next = (data ?? []).map((x) => x.value);
				if (typeof maxSelected === "number" && next.length > maxSelected) {
					return;
				}
				field.onChange(next);
			}}
			value={selectedOptions}
		/>
	);
};
